import {StructuredRemarksView} from './StructuredRemarksView'
import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import {useAuth} from '../../auth'
import {toast, ToastContainer} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import {KTSVG} from '../../../../_metronic/helpers'
import {getCompanyList, getCompanyAdminList} from '../../Management/core/_requests'
import './MonthEndReports.css'
import type {Vessel, Company, CompanyAdmin} from '../../Management/core/_models'
import type {
  MainEngineConsumptionDto,
  MainEngineEntryDto,
  MainEngineTrialType,
} from '../core/_models'
import {
  searchMainEngineConsumption,
  createMainEngineConsumption,
  updateMainEngineConsumption,
  deleteMainEngineConsumption,
  getMainEngineConsumptionDefaults,
} from '../core/_requests'
import DeleteMainEngineReportModal from './DeleteMainEngineReportModal'
import MainEngineActionConfirmModal from './MainEngineActionConfirmModal'
import MainEngineReviewModal from './MainEngineReviewModal'
import MainEngineTrialAttachmentsModal from './MainEngineTrialAttachmentsModal'
import { generateMainEngineConsumptionPdf } from './MainEngineConsumptionPdfGenerator'
import {useMonthEndFeatureAccess} from '../core/monthEndAccess'
import QhseAccessDenied from './QhseAccessDenied'
import SearchableSelect from '../../../shared/components/SearchableSelect'
import { useGlobalFilter } from '../../../shared/context/GlobalFilterContext'
import { TableSkeleton } from '../../../shared/components/LoadingSkeletons'
import {
  fetchAccessibleVesselsForCurrentUser,
  formatAccessibleVesselError,
  NO_ACCESSIBLE_VESSELS_MESSAGE,
  normalizeAccessibleVessels,
} from '../../../shared/access/accessibleVessels'

 const pickApiErrorMessage = (_err: any, fallback: string) => {
  return fallback
}

const currentYear = new Date().getFullYear()
const currentMonth = String(new Date().getMonth() + 1).padStart(2, '0')

type ModalMode = 'add' | 'edit' | 'view' | null

const emptyForm = (): MainEngineConsumptionDto => ({
  vesselId: null,
  reportMonth: new Date().getMonth() + 1,
  reportYear: currentYear,
  deadweight: '',
  eplImplementationDate: null,
  systemType: null,
  ballastSpeed12: null,
  ballastCons12: null,
  ballastRpm12: null,
  ladenSpeed12: null,
  ladenCons12: null,
  ladenRpm12: null,
  ballastSpeedEco: null,
  ballastConsEco: null,
  ballastRpmEco: null,
  ladenSpeedEco: null,
  ladenConsEco: null,
  ladenRpmEco: null,
  ballastSpeed83: null,
  ballastCons83: null,
  ballastRpm83: null,
  ladenSpeed83: null,
  ladenCons83: null,
  ladenRpm83: null,
  maxPowerEplMcrLim: null,
  maxConsumptionRefEexi: null,
  designSfocFromEexi: null,
  actualSfoc83: null,
  newAttainablePower: null,
  newSpeedVref: null,
  engineEntries: [
    {
      engineMakeModel: '',
      kw: null,
      ballastSpeed12: null,
      ballastCons12: null,
      ballastRpm12: null,
      ladenSpeed12: null,
      ladenCons12: null,
      ladenRpm12: null,
      ballastSpeedEco: null,
      ballastConsEco: null,
      ballastRpmEco: null,
      ladenSpeedEco: null,
      ladenConsEco: null,
      ladenRpmEco: null,
    },
  ],
})

const cloneEngineEntries = (entries?: MainEngineEntryDto[] | null): MainEngineEntryDto[] =>
  entries?.length
    ? entries.map((entry) => ({...entry}))
    : [{engineMakeModel: '', kw: null}]

const cloneMainEngineConsumption = (record: MainEngineConsumptionDto): MainEngineConsumptionDto => ({
  ...record,
  engineEntries: cloneEngineEntries(record.engineEntries),
})

const loadMainEngineConsumptionForm = async (
  vesselId: number | null,
  reportMonth: number | null | undefined,
  reportYear: number | null | undefined,
  fallbackForm: MainEngineConsumptionDto
): Promise<{form: MainEngineConsumptionDto; mode: 'add' | 'edit'; editId: number | null}> => {
  if (!vesselId) {
    return {form: fallbackForm, mode: 'add', editId: null}
  }

  if (reportMonth && reportYear) {
    try {
      const existing = await searchMainEngineConsumption({
        vesselId,
        month: reportMonth,
        year: reportYear,
        activeOnly: true,
      })

      if (existing.length > 0) {
        const record = existing[0]
        return {
          form: cloneMainEngineConsumption(record),
          mode: 'edit',
          editId: Number(record.id ?? 0) || null,
        }
      }
    } catch {
      // Fall back to vessel defaults when the month-specific lookup fails.
    }
  }

  try {
    const defaults = await getMainEngineConsumptionDefaults(vesselId)
    return {
      form: {
        ...fallbackForm,
        vesselId,
        deadweight: defaults.deadweight ?? fallbackForm.deadweight,
        eplImplementationDate: defaults.eplImplementationDate ?? fallbackForm.eplImplementationDate,
        systemType: defaults.systemType ?? fallbackForm.systemType,
        designSfocFromEexi: defaults.designSfocFromEexi ?? fallbackForm.designSfocFromEexi,
        engineEntries: defaults.engineEntries?.length
          ? cloneEngineEntries(defaults.engineEntries)
          : cloneEngineEntries(fallbackForm.engineEntries),
      },
      mode: 'add',
      editId: null,
    }
  } catch {
    return {
      form: {
        ...fallbackForm,
        vesselId,
        engineEntries: cloneEngineEntries(fallbackForm.engineEntries),
      },
      mode: 'add',
      editId: null,
    }
  }
}

const MONTHS_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const monthKeyToLabel = (key: string) => {
  const [y, m] = key.split('-')
  const mi = Number(m) - 1
  return `${MONTHS_SHORT[mi] ?? m} ${y}`
}

const recordMonthKey = (r: MainEngineConsumptionDto): string => {
  const y = r.reportYear
  const m = r.reportMonth
  if (!y || !m) return ''
  return `${y}-${String(m).padStart(2, '0')}`
}

const buildMonthRange = (fromKey: string, toKey: string): string[] => {
  if (!fromKey || !toKey) return []
  const [fy, fm] = fromKey.split('-').map(Number)
  const [ty, tm] = toKey.split('-').map(Number)
  if (!fy || !fm || !ty || !tm) return []
  let start = fy * 12 + (fm - 1)
  let end = ty * 12 + (tm - 1)
  if (start > end) [start, end] = [end, start]
  const out: string[] = []
  for (let cur = start; cur <= end; cur++) {
    const y = Math.floor(cur / 12)
    const m = (cur % 12) + 1
    out.push(`${y}-${String(m).padStart(2, '0')}`)
  }
  return out
}

const VIEW_LABEL = 'fw-semibold text-muted d-block mb-1 fs-8'
const VIEW_VALUE = 'fw-bold text-dark fs-7'

const renderViewField = (label: string, value: any, colSpan: number = 4) => (
  <div className={`col-md-${colSpan} mb-4`}>
    <label className={VIEW_LABEL}>{label}</label>
    <div className={VIEW_VALUE}>{value ?? '-'}</div>
  </div>
)

const formatMetric = (value?: number | null, unit?: string) => {
  if (value == null || Number.isNaN(value)) return '-'
  return `${Number(value).toFixed(1)}${unit ? ` ${unit}` : ''}`
}

const speedConsLabel = (title: string) => {
  const hasSpeedCons = title.includes('speed/cons')
  if (!hasSpeedCons) return <span>{title}</span>

  const [before, after] = title.split('speed/cons')
  return (
    <span>
      {before}
      <span className='text-primary fw-semibold'>speed</span>
      <span>/</span>
      <span className='text-success fw-semibold'>cons</span>
      {after}
    </span>
  )
}

const renderSpeedConsumption = (
  speed?: number | null,
  consumption?: number | null,
  rpm?: number | null,
  mode: 'ballast' | 'laden' = 'ballast'
) => {
  const hasAny = speed != null || consumption != null
  if (!hasAny) return '-'
  const speedStyle = {backgroundColor: '#dbeafe', color: '#1d4ed8', border: '1px solid #93c5fd'}
  const consumptionStyle = {backgroundColor: '#dcfce7', color: '#15803d', border: '1px solid #86efac'}
  return (
    <div
      className='d-inline-flex flex-column align-items-center text-center gap-1'
      style={{
        backgroundColor: '#f8fafc',
        border: '1px solid #d1d5db',
        borderRadius: '10px',
        padding: '6px 8px',
        minWidth: '88px',
      }}
    >
      <div className='d-flex gap-2 flex-nowrap justify-content-center'>
        {speed != null && <span className='badge fw-semibold text-nowrap' style={{fontSize: '10px', ...speedStyle}}>{formatMetric(speed, 'kn')}</span>}
        {consumption != null && <span className='badge fw-semibold text-nowrap' style={{fontSize: '10px', ...consumptionStyle}}>{formatMetric(consumption, 't/day')}</span>}
      </div>
      {/* RPM is stored but not displayed in the column view as per request */}
    </div>
  )
}

const ENGINE_BADGE_STYLE = {
  backgroundColor: '#ffffff',
  color: '#1f2937',
  border: '1px solid #d1d5db',
}

const ENGINE_CARD_STYLE: React.CSSProperties = {
  width: '100%',
  maxWidth: '96px',
  height: '40px',
  minHeight: '40px',
  backgroundColor: '#ffffff',
  border: '1px solid #d1d5db',
  borderRadius: '8px',
  padding: '4px 6px',
  display: 'flex',
  alignItems: 'center',
  boxSizing: 'border-box',
}

const nestedPivotCell = (items: any[] | undefined, render: (item: any, idx: number) => React.ReactNode) => {
  if (!items?.length) return '-'
  return (
    <div className='d-flex flex-column h-100 align-items-stretch gap-2'>
      {items.map((item, idx) => (
        <div 
          key={idx} 
          className='flex-grow-1 d-flex flex-column align-items-stretch justify-content-start position-relative'
          style={{ minHeight: '42px', width: '100%', padding: '5px 0 3px' }}
        >
          <span 
            className='position-absolute top-0 start-0 badge badge-circle fw-bold'
            style={{
              width: '15px',
              height: '15px',
              fontSize: '8px',
              opacity: 0.8,
              margin: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...ENGINE_BADGE_STYLE,
            }}
            title={`Engine #${idx + 1}`}
          >
            {idx + 1}
          </span>
          {render(item, idx)}
        </div>
      ))}
    </div>
  )
}

type PivotActions = {
  canEditRow: (r: MainEngineConsumptionDto) => boolean
  canDeleteRow: (r: MainEngineConsumptionDto) => boolean
  canSubmitRow: (r: MainEngineConsumptionDto) => boolean
  canReviewRow: (r: MainEngineConsumptionDto) => boolean
  handleOpenView: (r: MainEngineConsumptionDto) => void
  handleOpenEdit: (r: MainEngineConsumptionDto) => void
  handleDelete: (r: MainEngineConsumptionDto) => void
  handleDownloadPdf: (r: MainEngineConsumptionDto) => void
  requestSubmit: (id: number) => void
  requestReview: (r: MainEngineConsumptionDto) => void
}

const getBadgeStyle = (index: number) => {
  const colors = [
    { bg: '#f1faff', color: '#009ef7', border: '#009ef7' }, // Blue
    { bg: '#e8fff3', color: '#50cd89', border: '#50cd89' }, // Green
    { bg: '#f8f5ff', color: '#7239ea', border: '#7239ea' }, // Purple
    { bg: '#fff8dd', color: '#ffc700', border: '#ffc700' }, // Yellow
    { bg: '#ffeee8', color: '#f1416c', border: '#f1416c' }, // Red
    { bg: '#eff2f5', color: '#181c32', border: '#181c32' }  // Dark
  ]
  const c = colors[index % colors.length]
  return {
    backgroundColor: c.bg,
    color: c.color,
    border: `1px solid ${c.border}`
  }
}

const PIVOT_FIELDS: Array<{
  key: string
  label: React.ReactNode
  render: (r: MainEngineConsumptionDto | undefined) => React.ReactNode
}> = [
  {key: 'deadweight', label: 'Deadweight', render: (r) => (r?.deadweight ? r.deadweight : '-')},
  {
    key: 'engines',
    label: 'Engine(s)',
    render: (r) => nestedPivotCell(r?.engineEntries, (e, idx) => {
      const style = getBadgeStyle(idx)
      return (
      <div className='d-flex align-items-center gap-1 text-start' style={{ ...ENGINE_CARD_STYLE, backgroundColor: style.backgroundColor, border: style.border }}>
        <span className='position-absolute badge badge-circle fw-bold' style={{top: '50%', left: '4px', transform: 'translateY(-50%)', width: '18px', height: '18px', fontSize: '9px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#ffffff', color: style.color, border: style.border}}>{idx + 1}</span>
        <div style={{minWidth: 0, flex: 1, paddingLeft: '22px'}}>
          <div className='fw-bold text-nowrap' style={{fontSize: '10px', lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', color: style.color}}>{e.engineMakeModel || 'Main Engine'}</div>
        </div>
      </div>
    )}),
  },
  {
    key: 'system',
    label: 'System',
    render: (r) =>
      r?.systemType ? (
        <span
          className={`badge badge-light-${r.systemType === 'EPL' ? 'primary' : 'info'}`}
        >
          {r.systemType}
        </span>
      ) : (
        '-'
      ),
  },
  {key: 'epl', label: 'EPL Date', render: (r) => r?.eplImplementationDate || '-'},
  {
    key: 'bSpd12',
    label: speedConsLabel('Ballast Spd(speed/cons)'),
    render: (r) => nestedPivotCell(r?.engineEntries, (e) => renderSpeedConsumption(e.ballastSpeed12, e.ballastCons12, e.ballastRpm12, 'ballast')),
  },
  {
    key: 'lSpd12',
    label: speedConsLabel('Laden Spd(speed/cons)'),
    render: (r) => nestedPivotCell(r?.engineEntries, (e) => renderSpeedConsumption(e.ladenSpeed12, e.ladenCons12, e.ladenRpm12, 'laden')),
  },
  {
    key: 'bSpdEco',
    label: speedConsLabel('Ballast Spd(Eco)'),
    render: (r) => nestedPivotCell(r?.engineEntries, (e) => renderSpeedConsumption(e.ballastSpeedEco, e.ballastConsEco, e.ballastRpmEco, 'ballast')),
  },
  {
    key: 'lSpdEco',
    label: speedConsLabel('Laden Spd(Eco)'),
    render: (r) => nestedPivotCell(r?.engineEntries, (e) => renderSpeedConsumption(e.ladenSpeedEco, e.ladenConsEco, e.ladenRpmEco, 'laden')),
  },
  {
    key: 'bSpd83',
    label: speedConsLabel('Ballast Spd(83% MCR)'),
    render: (r) => renderSpeedConsumption(r?.ballastSpeed83, r?.ballastCons83, r?.ballastRpm83),
  },
  {
    key: 'lSpd83',
    label: speedConsLabel('Laden Spd(83% MCR)'),
    render: (r) => renderSpeedConsumption(r?.ladenSpeed83, r?.ladenCons83, r?.ladenRpm83),
  },
]

function renderPivotTable(
  monthKey: string,
  vesselCols: Vessel[],
  lookup: Map<string, MainEngineConsumptionDto>,
  actions: PivotActions
) {
  return (
    <div className='month-end-reports-wrapper'>
    <table className='table table-bordered align-middle mb-0'>
      <thead className='table-header text-start'>
        <tr>
          <th className='ps-4 text-nowrap' style={{minWidth: 220, background: '#f9fafb'}}>
            Field
          </th>
          {vesselCols.map((v) => (
            <th key={v.id} className='text-nowrap text-center' style={{minWidth: 180}}>
              {v.fleet_name || v.name}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className='table-body'>
        {PIVOT_FIELDS.map((f) => (
          <tr key={f.key}>
            <td className='ps-4 fw-semibold text-dark'>{f.label}</td>
            {vesselCols.map((v) => {
              const r = lookup.get(`${monthKey}::${v.id}`)
              return (
                <td key={v.id} className='text-center'>
                  {f.render(r)}
                </td>
              )
            })}
          </tr>
        ))}
        <tr>
          <td className='ps-4 fw-semibold text-dark'>Status</td>
          {vesselCols.map((v) => {
            const r = lookup.get(`${monthKey}::${v.id}`)
            return (
              <td key={v.id} className='text-center'>
                {r ? (
                  <span
                    className={`badge badge-light-${
                      r.status === 'REVIEWED'
                        ? 'success'
                        : r.status === 'SUBMITTED'
                        ? 'warning'
                        : 'primary'
                    } fw-bold`}
                  >
                    {r.status || 'DRAFT'}
                  </span>
                ) : (
                  '-'
                )}
              </td>
            )
          })}
        </tr>
        <tr>
          <td className='ps-4 fw-semibold text-dark'>Actions</td>
          {vesselCols.map((v) => {
            const r = lookup.get(`${monthKey}::${v.id}`)
            if (!r) {
              return (
                <td key={v.id} className='text-center text-muted'>
                  -
                </td>
              )
            }
            return (
              <td key={v.id}>
                <div className='d-flex gap-1 justify-content-center align-items-center flex-wrap'>
                  {r.status === 'REVIEWED' && (
                    <button
                      className='btn btn-icon btn-sm btn-active-color-primary'
                      title='Download PDF'
                      onClick={() => actions.handleDownloadPdf(r)}
                    >
                      <KTSVG
                        path='/media/icons/duotune/general/download.svg'
                        className='svg-icon-3'
                      />
                    </button>
                  )}
                  <button
                    className='btn btn-icon btn-sm'
                    title='View'
                    onClick={() => actions.handleOpenView(r)}
                  >
                    <KTSVG
                      path='/media/map/ph_eye.svg'
                      className='svg-icon-3 text-primary'
                    />
                  </button>
                  {actions.canEditRow(r) && (
                    <button
                      className='btn btn-icon btn-sm'
                      title='Edit'
                      onClick={() => actions.handleOpenEdit(r)}
                    >
                      <KTSVG path='/media/map/edit-active.svg' className='svg-icon-3' />
                    </button>
                  )}
                  {actions.canDeleteRow(r) && (
                    <button
                      className='btn btn-icon btn-sm'
                      title='Delete'
                      onClick={() => actions.handleDelete(r)}
                    >
                      <KTSVG
                        path='/media/map/red-cross-icon.svg'
                        className='svg-icon-3'
                      />
                    </button>
                  )}
                  {actions.canSubmitRow(r) && (
                    <button
                      className='btn btn-light-primary btn-sm'
                      onClick={() => actions.requestSubmit(Number(r.id))}
                    >
                      Submit
                    </button>
                  )}
                  {actions.canReviewRow(r) && (
                    <button
                      className='btn btn-light-success btn-sm'
                      onClick={() => actions.requestReview(r)}
                    >
                      Review
                    </button>
                  )}
                </div>
              </td>
            )
          })}
        </tr>
      </tbody>
    </table>
    </div>
  )
}

const MainEngineConsumptionPage: FC = () => {
  const {currentUser} = useAuth()
  const {
    canRead,
    canCreate,
    canUpdate,
    canDelete,
    hasAssignedVessel,
    assignedCompanyGroupId,
    assignedCompanyAdminId,
    isGlobalScope,
    isOfficeUser,
  } = useMonthEndFeatureAccess('main-engine')
  const isShipboardUser = hasAssignedVessel
  const accessDenied = !canRead

  const canMultiMonthForShipboard = isShipboardUser && (canCreate || canUpdate)
  const useMultiSelectFilters = isOfficeUser || canMultiMonthForShipboard
  const isRestrictedOfficeUser = isOfficeUser && !isGlobalScope

  const canAdd = canCreate
  const canEditRow = (r: MainEngineConsumptionDto) =>
    canUpdate && (isOfficeUser || (r.status === 'DRAFT' || !r.status))
  const canDeleteRow = (r: MainEngineConsumptionDto) =>
    canDelete &&
    r.status !== 'REVIEWED' &&
    (isOfficeUser || r.status === 'DRAFT' || !r.status)
  const canSubmitRow = (r: MainEngineConsumptionDto) =>
    canUpdate && (isShipboardUser || isGlobalScope) && (r.status === 'DRAFT' || !r.status)
  const canReviewRow = (r: MainEngineConsumptionDto) =>
    canUpdate && isOfficeUser && r.status === 'SUBMITTED'

  /* if (!canRead) {
    return (
      <QhseAccessDenied message='Access denied — you do not have permission to view Main Engine Consumption.' />
    )
  } */

  const [vessels, setVessels] = useState<Vessel[]>([])
  const [companyAdmins, setCompanyAdmins] = useState<CompanyAdmin[]>([])
  const [subCompanies, setSubCompanies] = useState<Company[]>([])
  const [records, setRecords] = useState<MainEngineConsumptionDto[]>([])

  const [filterVessel, setFilterVessel] = useState<string>('')
  const [filterVesselIds, setFilterVesselIds] = useState<number[]>([])
  const [filterMonthKeys, setFilterMonthKeys] = useState<string[]>([])
  const [filterMonthValue, setFilterMonthValue] = useState<string>('')

  const [compareMonthDropdownOpen, setCompareMonthDropdownOpen] = useState(false)
  const [filterCompanyGroup, setFilterCompanyGroup] = useState<string>('')
  const [compareViewYear, setCompareViewYear] = useState<number>(new Date().getFullYear())
  const [filterCompanyAdmin, setFilterCompanyAdmin] = useState<string>('')

  const { state: globalState } = useGlobalFilter()

  useEffect(() => {
    if (globalState.vessel?.id) {
      setFilterVessel(String(globalState.vessel.id))
      setFilterVesselIds([Number(globalState.vessel.id)])
    } else {
      setFilterVessel('')
      setFilterVesselIds([])
    }

    if (globalState.company?.id) {
      setFilterCompanyGroup(String(globalState.company.id))
    } else {
      setFilterCompanyGroup('')
    }
  }, [globalState.vessel, globalState.company])

  const [vesselDropdownOpen, setVesselDropdownOpen] = useState(false)
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false)
  const [modalCompanyGroup, setModalCompanyGroup] = useState<string>('')
  const [modalCompanyAdmin, setModalCompanyAdmin] = useState<string>('')

  // Seatrial / Shoptrial modal state
  const [trialModal, setTrialModal] = useState<{type: MainEngineTrialType; vesselId: number; vesselName?: string} | null>(null)

  const [modalMode, setModalMode] = useState<ModalMode>(null)
  const [form, setForm] = useState<MainEngineConsumptionDto>(emptyForm())
  const [editId, setEditId] = useState<number | null>(null)
  const [deletingRecord, setDeletingRecord] = useState<MainEngineConsumptionDto | null>(null)
  const [submitTargetId, setSubmitTargetId] = useState<number | null>(null)
  const [reviewingRecord, setReviewingRecord] = useState<MainEngineConsumptionDto | null>(null)
  const [isReviewSaving, setIsReviewSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const mounted = useRef(true)

  const selectedMonthSummary = useMemo(
    () => filterMonthKeys.slice().sort().map((monthKey) => monthKeyToLabel(monthKey)).join(', '),
    [filterMonthKeys]
  )
  const selectedVesselSummary = useMemo(() => {
    const selectedVessels = useMultiSelectFilters
      ? vessels.filter((v) => filterVesselIds.includes(Number(v.id)))
      : filterVessel
        ? vessels.filter((v) => String(v.id) === filterVessel)
        : []

    return selectedVessels
      .map((v) => v.fleet_name || v.name || '')
      .filter(Boolean)
      .join(', ')
  }, [filterVessel, filterVesselIds, useMultiSelectFilters, vessels])
  const filterSummaryMessage =
    selectedMonthSummary && selectedVesselSummary
      ? `You are filling for ${selectedMonthSummary} for ${selectedVesselSummary}.`
      : ''

  const handleVesselMultiSelectChange = (nextVesselIds: number[]) => {
    // Prevent confusing comparison matrix: allow only one multi-dimension at a time.
    if (nextVesselIds.length > 1 && filterMonthKeys.length > 1) {
      setFilterMonthKeys((prev) => prev.slice(0, 1))
      toast.info('Only one month can be selected when multiple vessels are selected.', {
        position: 'top-center',
      })
    }
    setFilterVesselIds(nextVesselIds)
  }

  const handleMonthMultiSelectChange = (nextMonthKeys: string[]) => {
    // Prevent confusing comparison matrix: allow only one multi-dimension at a time.
    if (nextMonthKeys.length > 1 && filterVesselIds.length > 1) {
      setFilterVesselIds((prev) => prev.slice(0, 1))
      toast.info('Only one vessel can be selected when multiple months are selected.', {
        position: 'top-center',
      })
    }
    setFilterMonthKeys(nextMonthKeys)
  }



  useEffect(() => {
    return () => {
      mounted.current = false
    }
  }, [])

  // Load companies for Super Admin
  useEffect(() => {
    if (!isGlobalScope) return
    const loadCompanies = async () => {
      try {
        const [cga, ca] = await Promise.all([getCompanyAdminList(), getCompanyList()])
        if (mounted.current) {
          setCompanyAdmins(cga)
          setSubCompanies(ca)
        }
      } catch (err) {
        console.error('Failed to load companies', err)
      }
    }
    loadCompanies()
  }, [isGlobalScope])

  // Load vessels
  useEffect(() => {
    const load = async () => {
      try {
        const list = normalizeAccessibleVessels(await fetchAccessibleVesselsForCurrentUser())
        if (!mounted.current) return
        if (isShipboardUser) {
          const myVId = currentUser?.vessel?.id
          const accessibleVesselId = myVId && list.some((v) => Number(v.id) === Number(myVId)) ? String(myVId) : ''
          setVessels(list)
          if (accessibleVesselId) setFilterVessel(accessibleVesselId)
        } else {
          let filtered = list.filter((v: Vessel) => v.active ?? true)
          if (!isGlobalScope) {
            if (assignedCompanyGroupId) {
              filtered = filtered.filter((v) => Number(v.companyGroupAdmin?.id) === Number(assignedCompanyGroupId))
            }
            if (assignedCompanyAdminId) {
              filtered = filtered.filter((v) => Number(v.companyAdmin?.id) === Number(assignedCompanyAdminId))
            }
          }
          if (filterCompanyGroup) {
            filtered = filtered.filter(
              (v) => Number(v.companyGroupAdmin?.id) === Number(filterCompanyGroup)
            )
          }
          if (filterCompanyAdmin) {
            filtered = filtered.filter(
              (v) => Number(v.companyAdmin?.id) === Number(filterCompanyAdmin)
            )
          }
          setVessels(filtered)
        }
      } catch (error) {
        setVessels([])
        toast.error(formatAccessibleVesselError(error, 'Failed to load vessel list'), {
          position: 'top-center',
        })
      }
    }
    load()
  }, [isShipboardUser, isGlobalScope, assignedCompanyGroupId, assignedCompanyAdminId, currentUser, filterCompanyGroup, filterCompanyAdmin])

  useEffect(() => {
    if (useMultiSelectFilters) {
      setFilterVesselIds((prev) => prev.filter((id) => vessels.some((v) => Number(v.id) === Number(id))))
      return
    }
    if (filterVessel && !vessels.some((v) => String(v.id) === filterVessel)) {
      setFilterVessel('')
    }
  }, [filterVessel, useMultiSelectFilters, vessels])

  useEffect(() => {
    if (isGlobalScope && filterMonthValue) {
      setFilterMonthValue('')
    }
  }, [isGlobalScope, filterMonthValue])

  // Load records (for multi-select mode we fetch all in scope and filter client-side)
  const loadRecords = async () => {
    try {
      setLoading(true)
      const params: any = {}
      if (isShipboardUser) {
        params.vesselId = Number(currentUser?.vessel?.id)
      } else if (!useMultiSelectFilters && filterVessel) {
        params.vesselId = Number(filterVessel)
      }
      if (filterCompanyGroup) params.companyGroupId = Number(filterCompanyGroup)
      if (filterCompanyAdmin) params.companyAdminId = Number(filterCompanyAdmin)
      
      if (!isGlobalScope) {
        if (assignedCompanyGroupId) params.companyGroupId = Number(assignedCompanyGroupId)
        if (assignedCompanyAdminId) params.companyAdminId = Number(assignedCompanyAdminId)
      }
      const data = await searchMainEngineConsumption(params)
      if (mounted.current) setRecords(data)
    } catch {
      if (mounted.current) setRecords([])
      toast.error('Failed to load reports', {position: 'top-center'})
    } finally {
      if (mounted.current) setLoading(false)
    }
  }

  useEffect(() => {
    loadRecords()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filterVessel,
    filterMonthValue,
    filterCompanyGroup,
    filterCompanyAdmin,
    useMultiSelectFilters,
  ])

  // Client-side filtered records for multi-select mode
  const filteredRecords = useMemo(() => {
    return records.filter((r) => {
      const statusVisibleToUser =
        !isRestrictedOfficeUser || r.status === 'SUBMITTED' || r.status === 'REVIEWED'

      const vesselMatch = useMultiSelectFilters
        ? filterVesselIds.length === 0 ||
          (r.vesselId != null && filterVesselIds.includes(Number(r.vesselId)))
        : !filterVessel || Number(r.vesselId) === Number(filterVessel)

      const key = recordMonthKey(r)
      const explicitMonthMatch = useMultiSelectFilters
        ? filterMonthKeys.length === 0 || (key !== '' && filterMonthKeys.includes(key))
        : true

      const singleMonthMatch =
        useMultiSelectFilters ||
        !filterMonthValue ||
        (key !== '' && key === filterMonthValue)

      return (
        statusVisibleToUser &&
        vesselMatch &&
        explicitMonthMatch &&
        singleMonthMatch
      )
    })
  }, [
    records,
    isRestrictedOfficeUser,
    filterVesselIds,
    filterMonthKeys,
    useMultiSelectFilters,
    filterVessel,
    filterMonthValue,
  ])

  // Available month keys for selectors (full month range, not limited to created reports only)
  const availableMonthKeys = useMemo(() => {
    const years = records
      .map((r) => Number(r.reportYear))
      .filter((y) => Number.isFinite(y) && y > 0)
    const minYear = years.length ? Math.min(...years, currentYear - 1) : currentYear - 1
    const maxYear = years.length ? Math.max(...years, currentYear + 1) : currentYear + 1

    const keys: string[] = []
    for (let y = minYear; y <= maxYear; y++) {
      for (let m = 1; m <= 12; m++) {
        keys.push(`${y}-${String(m).padStart(2, '0')}`)
      }
    }

    return keys.sort((a, b) => (a < b ? 1 : -1))
  }, [records])

  // Selected vessel list for pivot (if none selected → show all available)
  const pivotVessels = useMemo(() => {
    if (!useMultiSelectFilters) return vessels
    if (filterVesselIds.length === 0) return vessels
    const set = new Set(filterVesselIds)
    return vessels.filter((v) => set.has(Number(v.id)))
  }, [vessels, filterVesselIds, useMultiSelectFilters])

  // Pivot month keys to render. If none selected, derive from filteredRecords (unique)
  const pivotMonthKeys = useMemo(() => {
    if (useMultiSelectFilters && filterMonthKeys.length > 0) {
      return [...filterMonthKeys].sort((a, b) => (a < b ? 1 : -1))
    }
    const set = new Set<string>()
    filteredRecords.forEach((r) => {
      const k = recordMonthKey(r)
      if (k) set.add(k)
    })
    return Array.from(set).sort((a, b) => (a < b ? 1 : -1))
  }, [useMultiSelectFilters, filterMonthKeys, filteredRecords])

  // Map (monthKey, vesselId) -> record for pivot lookup
  const pivotMap = useMemo(() => {
    const map = new Map<string, MainEngineConsumptionDto>()
    filteredRecords.forEach((r) => {
      const k = recordMonthKey(r)
      const vid = r.vesselId != null ? String(r.vesselId) : ''
      if (k && vid) map.set(`${k}::${vid}`, r)
    })
    return map
  }, [filteredRecords])

  const getModalFilteredVessels = (companyGroupId: string, companyAdminId: string) =>
    vessels.filter((v: Vessel) => {
      if (companyGroupId && Number(v.companyGroupAdmin?.id) !== Number(companyGroupId)) return false
      if (companyAdminId && Number(v.companyAdmin?.id) !== Number(companyAdminId)) return false
      return true
    })

  const modalVessels = useMemo(() => {
    if (!(isGlobalScope && modalMode === 'add')) return vessels
    return getModalFilteredVessels(modalCompanyGroup, modalCompanyAdmin)
  }, [isGlobalScope, modalMode, vessels, modalCompanyGroup, modalCompanyAdmin])

  const filterSubCompanies = useMemo(
    () =>
      subCompanies.filter(
        (sc) => !filterCompanyGroup || Number(sc.cga?.id) === Number(filterCompanyGroup)
      ),
    [subCompanies, filterCompanyGroup]
  )
  const showFilterSubCompany = !filterCompanyGroup || filterSubCompanies.length > 0

  const modalSubCompanies = useMemo(
    () =>
      subCompanies.filter(
        (sc) => !modalCompanyGroup || Number(sc.cga?.id) === Number(modalCompanyGroup)
      ),
    [subCompanies, modalCompanyGroup]
  )
  const showModalSubCompany = !modalCompanyGroup || modalSubCompanies.length > 0

  // Office review workflow
  const handleCloseReviewModal = () => {
    setReviewingRecord(null)
  }

  const handleSaveReviewOnly = async (officeRemarks: string) => {
    if (!reviewingRecord?.id) return
    try {
      setIsReviewSaving(true)
      await updateMainEngineConsumption(Number(reviewingRecord.id), {
        officeRemarks: officeRemarks.trim(),
      })
      toast.success('Office review saved')
      handleCloseReviewModal()
      loadRecords()
    } catch (err) {
      toast.error(pickApiErrorMessage(err, 'Failed to save office review'))
    } finally {
      setIsReviewSaving(false)
    }
  }

  const handleCloseSatisfactory = async (officeRemarks: string) => {
    if (!reviewingRecord?.id) return
    try {
      setIsReviewSaving(true)
      await updateMainEngineConsumption(Number(reviewingRecord.id), {
        officeRemarks: officeRemarks.trim(),
        status: 'REVIEWED',
      })
      toast.success('Report closed satisfactorily')
      handleCloseReviewModal()
      loadRecords()
    } catch (err) {
      toast.error(pickApiErrorMessage(err, 'Failed to close report satisfactorily'))
    } finally {
      setIsReviewSaving(false)
    }
  }

  const confirmSubmit = async () => {
    if (!submitTargetId) return
    try {
      await updateMainEngineConsumption(submitTargetId, {status: 'SUBMITTED'})
      toast.success('Report submitted to Office')
      setSubmitTargetId(null)
      loadRecords()
    } catch (err) {
      toast.error('Failed to submit report')
    }
  }

  const requestReview = (record: MainEngineConsumptionDto) => setReviewingRecord(record)

  const requestSubmit = (id: number) => setSubmitTargetId(id)

  const handleOpenAdd = async () => {
    const f = emptyForm()

    const initialCompanyGroup = isGlobalScope ? filterCompanyGroup : ''
    const initialCompanyAdmin = isGlobalScope ? filterCompanyAdmin : ''
    setModalCompanyGroup(initialCompanyGroup)
    setModalCompanyAdmin(initialCompanyAdmin)

    const addVessels = isGlobalScope
      ? getModalFilteredVessels(initialCompanyGroup, initialCompanyAdmin)
      : vessels

    const preferredVesselId = filterVessel ? Number(filterVessel) : null
    const vesselId =
      preferredVesselId && addVessels.some((v: Vessel) => Number(v.id) === preferredVesselId)
        ? preferredVesselId
        : addVessels[0]?.id
        ? Number(addVessels[0].id)
        : null

    f.vesselId = vesselId
    const next = await loadMainEngineConsumptionForm(vesselId, f.reportMonth, f.reportYear, f)
    setForm(next.form)
    setEditId(next.editId)
    setModalMode(next.mode)
  }

  const handleOpenEdit = (r: MainEngineConsumptionDto) => {
    setForm({
      ...r,
      engineEntries: r.engineEntries?.length
        ? [...r.engineEntries]
        : [{engineMakeModel: '', kw: null}],
    })
    setEditId(Number(r.id))
    setModalMode('edit')
  }

  const handleOpenView = (r: MainEngineConsumptionDto) => {
    setForm(r)
    setModalMode('view')
  }

  const handleDelete = (record: MainEngineConsumptionDto) => {
    setDeletingRecord(record)
  }

  const handleDownloadPdf = (report: MainEngineConsumptionDto) => {
    const vessel = vessels.find((v) => Number(v.id) === Number(report.vesselId))
    const vesselName = vessel?.fleet_name || vessel?.name || `Vessel ${report.vesselId}`

    try {
      generateMainEngineConsumptionPdf(report, vesselName)
      toast.success('PDF downloaded successfully', { position: 'top-center' })
    } catch (error) {
      console.error('Failed to generate PDF:', error)
      toast.error('Failed to generate PDF', { position: 'top-center' })
    }
  }

  const confirmDelete = async () => {
    if (!deletingRecord?.id) return
    try {
      await deleteMainEngineConsumption(Number(deletingRecord.id))
      toast.success('Report deleted', {position: 'top-center'})
      setDeletingRecord(null)
      loadRecords()
    } catch {
      toast.error('Failed to delete', {position: 'top-center'})
    }
  }

  const handleSave = async () => {
    if (!form.vesselId) {
      toast.error('Please select a vessel', {position: 'top-center'})
      return
    }
    if (!form.reportMonth || !form.reportYear) {
      toast.error('Month and Year are required', {position: 'top-center'})
      return
    }
    try {
      if (modalMode === 'add') {
        await createMainEngineConsumption(form)
        toast.success('Report created', {position: 'top-center'})
      } else if (modalMode === 'edit' && editId) {
        await updateMainEngineConsumption(editId, form)
        toast.success('Report updated', {position: 'top-center'})
      }
      setModalMode(null)
      loadRecords()
    } catch (e: any) {
      toast.error(pickApiErrorMessage(e, 'Failed to save'), {position: 'top-center'})
    }
  }

  const setField = (key: string, value: any) => setForm((prev) => ({...prev, [key]: value}))

  const setEngineEntry = (idx: number, key: keyof MainEngineEntryDto, val: any) => {
    setForm((prev) => {
      const entries = [...(prev.engineEntries || [])]
      entries[idx] = {...entries[idx], [key]: val}
      return {...prev, engineEntries: entries}
    })
  }

  const addEngineEntry = () =>
    setForm((prev) => ({
      ...prev,
      engineEntries: [
        ...(prev.engineEntries || []),
        {
          engineMakeModel: '',
          kw: null,
          ballastSpeed12: null,
          ballastCons12: null,
          ballastRpm12: null,
          ladenSpeed12: null,
          ladenCons12: null,
          ladenRpm12: null,
          ballastSpeedEco: null,
          ballastConsEco: null,
          ballastRpmEco: null,
          ladenSpeedEco: null,
          ladenConsEco: null,
          ladenRpmEco: null,
        },
      ],
    }))

  const removeEngineEntry = (idx: number) =>
    setForm((prev) => ({
      ...prev,
      engineEntries: (prev.engineEntries || []).filter((_, i) => i !== idx),
    }))

  // Pagination (only used in non-pivot mode)
  const totalPages = Math.ceil(filteredRecords.length / rowsPerPage)
  const pageRecords = filteredRecords.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  )
  const usePivotLayout = false
  const showVesselColumnForTable = isOfficeUser

  const isReadOnly = modalMode === 'view'
  const vesselOptions = isGlobalScope && modalMode === 'add' ? modalVessels : vessels

  /** Determines which vessel to open trial attachments for (single choice). */
  const resolveTrialVesselId = (): number | null => {
    // Crew: always their own vessel
    if (isShipboardUser) {
      const vid = (currentUser as any)?.vessel?.id
      return vid ? Number(vid) : null
    }
    // Single-select filter
    if (filterVessel) return Number(filterVessel)
    // Multi-select: require exactly one selected
    if (useMultiSelectFilters && filterVesselIds.length === 1) return filterVesselIds[0]
    // If only one vessel available, use it
    if (vessels.length === 1) return Number(vessels[0].id)
    return null
  }

  // Number input helper
  const numInput = (key: string, label: string, val: any, disabled = false) => (
    <div className='col-md-4 col-lg-3 mb-4' key={key}>
      <label className='form-label fw-semibold fs-7 text-dark'>{label}</label>
      <input
        type='number'
        step='any'
        className='form-control form-control-sm text-dark'
        style={{backgroundColor: '#fff'}}
        value={val ?? ''}
        disabled={isReadOnly || disabled}
        onChange={(e) => setField(key, e.target.value ? Number(e.target.value) : null)}
      />
    </div>
  )

  const nestedNumInput = (idx: number, key: keyof MainEngineEntryDto, label: string, val: any) => (
    <div className='col-md-4 mb-3' key={`${idx}-${key}`}>
      <label className='form-label fw-semibold fs-8 text-dark'>{label}</label>
      <input
        type='number'
        step='any'
        className='form-control form-control-sm text-dark'
        style={{backgroundColor: '#fff'}}
        value={val ?? ''}
        disabled={isReadOnly}
        onChange={(e) => setEngineEntry(idx, key, e.target.value ? Number(e.target.value) : null)}
      />
    </div>
  )

  const renderPeriodFilters = () => {
    if (useMultiSelectFilters) {
      if (canMultiMonthForShipboard) {
        return (
          <div className='col-md-4'>
            <label className='form-label fw-bold text-dark'>Months Filter</label>
            <div className='position-relative'>
              <button
                type='button'
                className='form-select form-select-sm text-start'
                onClick={() => {
                  setCompareMonthDropdownOpen((o) => !o)
                  setVesselDropdownOpen(false)
                }}
              >
                {filterMonthKeys.length > 0 ? `${filterMonthKeys.length} month(s) selected` : 'Select months'}
              </button>
              {compareMonthDropdownOpen && (
                <div
                  className='position-absolute bg-white border rounded shadow-sm w-100 mt-1 p-2'
                  style={{zIndex: 20, maxHeight: 280, overflowY: 'auto'}}
                >
                  <div className='d-flex flex-column gap-3'>
                    <div className='d-flex align-items-center justify-content-between bg-light rounded p-2 border'>
                      <button
                        type='button'
                        className='btn btn-xs btn-icon btn-active-light-primary'
                        onClick={() => setCompareViewYear((prev) => prev - 1)}
                      >
                        <i className='bi bi-chevron-left'></i>
                      </button>
                      <div className='d-flex align-items-center gap-2'>
                        <input
                          type='number'
                          className='form-control form-control-sm py-0 px-1 text-center fw-bolder border-0 bg-transparent'
                          style={{width: '60px', fontSize: '15px'}}
                          value={compareViewYear}
                          onChange={(e) => setCompareViewYear(Number(e.target.value))}
                        />
                        <button
                          className='btn btn-xs btn-link p-0 fs-9 text-decoration-none'
                          onClick={() => {
                            const yearKeys = MONTHS_SHORT.map(
                              (_, idx) => `${compareViewYear}-${String(idx + 1).padStart(2, '0')}`
                            )
                            const allSelected = yearKeys.every((k) => filterMonthKeys.includes(k))
                            if (allSelected) {
                              handleMonthMultiSelectChange(filterMonthKeys.filter((k) => !yearKeys.includes(k)))
                            } else {
                              handleMonthMultiSelectChange(Array.from(new Set([...filterMonthKeys, ...yearKeys])))
                            }
                          }}
                        >
                          Toggle All
                        </button>
                      </div>
                      <button
                        type='button'
                        className='btn btn-xs btn-icon btn-active-light-primary'
                        onClick={() => setCompareViewYear((prev) => prev + 1)}
                      >
                        <i className='bi bi-chevron-right'></i>
                      </button>
                    </div>
                    <div className='d-grid' style={{gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px'}}>
                      {MONTHS_SHORT.map((mLabel, idx) => {
                        const mk = `${compareViewYear}-${String(idx + 1).padStart(2, '0')}`
                        const selected = filterMonthKeys.includes(mk)
                        return (
                          <button
                            key={mk}
                            type='button'
                            className={`btn btn-sm py-2 px-0 border transition-all d-flex flex-column align-items-center justify-content-center ${
                              selected
                                ? 'btn-primary border-primary shadow-sm'
                                : 'btn-light border-gray-200 text-muted hover:border-primary'
                            }`}
                            style={{fontSize: '11px'}}
                            onClick={() => {
                              const next = selected
                                ? filterMonthKeys.filter((k) => k !== mk)
                                : [...filterMonthKeys, mk]
                              handleMonthMultiSelectChange(next)
                            }}
                          >
                            <span className='fw-bold'>{mLabel}</span>
                          </button>
                        )
                      })}
                    </div>
                    {filterMonthKeys.length > 0 && (
                      <div className='mt-2 border-top pt-3'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                          <label className='fs-9 fw-bold text-muted text-uppercase'>Selected Periods</label>
                          <button
                            className='btn btn-xs btn-link p-0 fs-9'
                            onClick={() => handleMonthMultiSelectChange([])}
                          >
                            Clear All
                          </button>
                        </div>
                        <div className='d-flex flex-wrap gap-1' style={{maxHeight: '100px', overflowY: 'auto'}}>
                          {Array.from(new Set(filterMonthKeys.map((k) => k.split('-')[0])))
                            .sort((a, b) => (a < b ? 1 : -1))
                            .map((year) => {
                              const yearMonths = filterMonthKeys
                                .filter((k) => k.startsWith(year + '-'))
                                .map((k) => MONTHS_SHORT[Number(k.split('-')[1]) - 1])
                                .join(', ')
                              return (
                                <div
                                  key={year}
                                  className='badge badge-light-primary d-flex align-items-center gap-1 py-1 px-2 border border-primary border-opacity-10'
                                >
                                  <span className='fw-bold'>{year}:</span>
                                  <span
                                    className='text-dark fs-9'
                                    style={{maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                                    title={yearMonths}
                                  >
                                    {yearMonths}
                                  </span>
                                  <i
                                    className='bi bi-x fs-7 cursor-pointer hover:text-danger'
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleMonthMultiSelectChange(
                                        filterMonthKeys.filter((k) => !k.startsWith(year + '-'))
                                      )
                                    }}
                                  ></i>
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )
      }

      return (
        <>
          <div className={isGlobalScope ? (showFilterSubCompany ? 'col-md-3' : 'col-md-4') : 'col-md-6'}>
            <label className='form-label fw-bold text-dark'>Vessel Selection</label>
            <div className='position-relative'>
              <button
                type='button'
                className='form-select form-select-sm text-start'
                onClick={() => {
                  setVesselDropdownOpen((o) => !o)
                  setCompareMonthDropdownOpen(false)
                }}
              >
                {filterVesselIds.length === 0
                  ? 'All Vessels'
                  : `${filterVesselIds.length} vessel(s) selected`}
              </button>
              {vesselDropdownOpen && (
                <div
                  className='position-absolute bg-white border rounded shadow-sm w-100 mt-1 p-2'
                  style={{zIndex: 20, maxHeight: 260, overflowY: 'auto'}}
                >
                  {vessels.length === 0 ? (
                    <div className='p-2 text-muted text-center fs-8'>{NO_ACCESSIBLE_VESSELS_MESSAGE}</div>
                  ) : (
                    vessels.map((v) => {
                      const vid = Number(v.id)
                      const checked = filterVesselIds.includes(vid)
                      return (
                        <div
                          key={vid}
                          className='form-check mb-1'
                          onClick={() =>
                            handleVesselMultiSelectChange(
                              checked ? filterVesselIds.filter((x) => x !== vid) : [...filterVesselIds, vid]
                            )
                          }
                          style={{cursor: 'pointer'}}
                        >
                          <input
                            type='checkbox'
                            className='form-check-input'
                            checked={checked}
                            readOnly
                          />
                          <label className='form-check-label fs-8'>{v.fleet_name || v.name}</label>
                        </div>
                      )
                    })
                  )}
                </div>
              )}
            </div>
            {filterSummaryMessage && (
              <div className='alert alert-light-info border border-info border-opacity-10 d-flex align-items-start gap-2 py-2 px-3 mt-2 mb-0 fs-8 text-dark'>
                <i className='bi bi-info-circle-fill text-info mt-1'></i>
                <span>{filterSummaryMessage}</span>
              </div>
            )}
          </div>
          <div className={isGlobalScope ? (showFilterSubCompany ? 'col-md-3' : 'col-md-4') : 'col-md-6'}>
            <label className='form-label fw-bold text-dark'>Months Filter</label>
            <div className='position-relative'>
              <button
                type='button'
                className='form-select form-select-sm text-start'
                onClick={() => {
                  setCompareMonthDropdownOpen((o) => !o)
                  setVesselDropdownOpen(false)
                }}
              >
                {filterMonthKeys.length > 0 ? `${filterMonthKeys.length} month(s) selected` : 'Select months'}
              </button>
              {compareMonthDropdownOpen && (
                <div
                  className='position-absolute bg-white border rounded shadow-sm w-100 mt-1 p-2'
                  style={{zIndex: 20, maxHeight: 280, overflowY: 'auto'}}
                >
                  <div className='d-flex flex-column gap-3'>
                    <div className='d-flex align-items-center justify-content-between bg-light rounded p-2 border'>
                      <button
                        type='button'
                        className='btn btn-xs btn-icon btn-active-light-primary'
                        onClick={() => setCompareViewYear((prev) => prev - 1)}
                      >
                        <i className='bi bi-chevron-left'></i>
                      </button>
                      <div className='d-flex align-items-center gap-2'>
                        <input
                          type='number'
                          className='form-control form-control-sm py-0 px-1 text-center fw-bolder border-0 bg-transparent'
                          style={{width: '60px', fontSize: '15px'}}
                          value={compareViewYear}
                          onChange={(e) => setCompareViewYear(Number(e.target.value))}
                        />
                        <button
                          className='btn btn-xs btn-link p-0 fs-9 text-decoration-none'
                          onClick={() => {
                            const yearKeys = MONTHS_SHORT.map(
                              (_, idx) => `${compareViewYear}-${String(idx + 1).padStart(2, '0')}`
                            )
                            const allSelected = yearKeys.every((k) => filterMonthKeys.includes(k))
                            if (allSelected) {
                              handleMonthMultiSelectChange(filterMonthKeys.filter((k) => !yearKeys.includes(k)))
                            } else {
                              handleMonthMultiSelectChange(Array.from(new Set([...filterMonthKeys, ...yearKeys])))
                            }
                          }}
                        >
                          Toggle All
                        </button>
                      </div>
                      <button
                        type='button'
                        className='btn btn-xs btn-icon btn-active-light-primary'
                        onClick={() => setCompareViewYear((prev) => prev + 1)}
                      >
                        <i className='bi bi-chevron-right'></i>
                      </button>
                    </div>
                    <div className='d-grid' style={{gridTemplateColumns: 'repeat(3, 1fr)', gap: '6px'}}>
                      {MONTHS_SHORT.map((mLabel, idx) => {
                        const mk = `${compareViewYear}-${String(idx + 1).padStart(2, '0')}`
                        const selected = filterMonthKeys.includes(mk)
                        return (
                          <button
                            key={mk}
                            type='button'
                            className={`btn btn-sm py-2 px-0 border transition-all d-flex flex-column align-items-center justify-content-center ${
                              selected
                                ? 'btn-primary border-primary shadow-sm'
                                : 'btn-light border-gray-200 text-muted hover:border-primary'
                            }`}
                            style={{fontSize: '11px'}}
                            onClick={() => {
                              const next = selected
                                ? filterMonthKeys.filter((k) => k !== mk)
                                : [...filterMonthKeys, mk]
                              handleMonthMultiSelectChange(next)
                            }}
                          >
                            <span className='fw-bold'>{mLabel}</span>
                          </button>
                        )
                      })}
                    </div>
                    {filterMonthKeys.length > 0 && (
                      <div className='mt-2 border-top pt-3'>
                        <div className='d-flex justify-content-between align-items-center mb-2'>
                          <label className='fs-9 fw-bold text-muted text-uppercase'>Selected Periods</label>
                          <button
                            className='btn btn-xs btn-link p-0 fs-9'
                            onClick={() => handleMonthMultiSelectChange([])}
                          >
                            Clear All
                          </button>
                        </div>
                        <div className='d-flex flex-wrap gap-1' style={{maxHeight: '100px', overflowY: 'auto'}}>
                          {Array.from(new Set(filterMonthKeys.map((k) => k.split('-')[0])))
                            .sort((a, b) => (a < b ? 1 : -1))
                            .map((year) => {
                              const yearMonths = filterMonthKeys
                                .filter((k) => k.startsWith(year + '-'))
                                .map((k) => MONTHS_SHORT[Number(k.split('-')[1]) - 1])
                                .join(', ')
                              return (
                                <div
                                  key={year}
                                  className='badge badge-light-primary d-flex align-items-center gap-1 py-1 px-2 border border-primary border-opacity-10'
                                >
                                  <span className='fw-bold'>{year}:</span>
                                  <span
                                    className='text-dark fs-9'
                                    style={{maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}
                                    title={yearMonths}
                                  >
                                    {yearMonths}
                                  </span>
                                  <i
                                    className='bi bi-x fs-7 cursor-pointer hover:text-danger'
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      handleMonthMultiSelectChange(
                                        filterMonthKeys.filter((k) => !k.startsWith(year + '-'))
                                      )
                                    }}
                                  ></i>
                                </div>
                              )
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )
    }

    return (
      <>
        {!canMultiMonthForShipboard && (
          <div
            className={isGlobalScope ? (showFilterSubCompany ? 'col-md-3' : 'col-md-4') : 'col-md-6'}
          >
            <label className='form-label fw-bold text-dark'>Vessel</label>
            <SearchableSelect
              className='form-select form-select-sm'
              value={filterVessel}
              onChange={(e) => setFilterVessel(e.target.value)}
              disabled={isShipboardUser}
            >
              {!isShipboardUser && <option value=''>All Vessels</option>}
              {vessels.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.fleet_name || v.name}
                </option>
              ))}
            </SearchableSelect>
          </div>
        )}
        <div
          className={
            isGlobalScope
              ? showFilterSubCompany
                ? 'col-md-3'
                : 'col-md-4'
              : 'col-md-6'
          }
        >
          <label className='form-label fw-bold text-dark'>Month/Year</label>
          <input
            type='month'
            className='form-control form-control-sm'
            style={{maxWidth: '50%'}}
            value={filterMonthValue}
            onChange={(e) => setFilterMonthValue(e.target.value)}
          />
        </div>
      </>
    )
  }

  return accessDenied ? (
    <QhseAccessDenied message='Access denied â€” you do not have permission to view Main Engine Consumption.' />
  ) : (
    <>
      <ToastContainer />

      {/* Main Content */}
      <div className='card'>
        <div className='card-header border-0 pt-6 d-flex justify-content-between bg-white'>
          <div>
            <h3 className='card-label text-dark fw-bold'>Main Engine Consumption</h3>
            <span className='text-muted fs-7'>
              View and manage monthly main engine consumption reports
            </span>
          </div>
          <div className='card-toolbar d-flex align-items-center gap-3'>
            {canAdd && (
              <button className='btn btn_primary' onClick={handleOpenAdd}>
                <i className='bi bi-plus-lg me-1'></i>Add Report
              </button>
            )}
            <button
              className='btn btn-light-primary'
              onClick={() => {
                const vid = resolveTrialVesselId()
                if (!vid) {
                  toast.info('Please select a vessel first', {position: 'top-center'})
                  return
                }
                const v = vessels.find((x) => Number(x.id) === vid)
                setTrialModal({
                  type: 'SEATRIAL',
                  vesselId: vid,
                  vesselName: v?.fleet_name || v?.name,
                })
              }}
            >
              <i className='bi bi-file-earmark-arrow-up me-1'></i>Seatrial Report
            </button>
            <button
              className='btn btn-light-primary'
              onClick={() => {
                const vid = resolveTrialVesselId()
                if (!vid) {
                  toast.info('Please select a vessel first', {position: 'top-center'})
                  return
                }
                const v = vessels.find((x) => Number(x.id) === vid)
                setTrialModal({
                  type: 'SHOPTRIAL',
                  vesselId: vid,
                  vesselName: v?.fleet_name || v?.name,
                })
              }}
            >
              <i className='bi bi-file-earmark-arrow-up me-1'></i>Shoptrial Report
            </button>
          </div>
        </div>
        <div className='card-body py-5 bg-white border-top'>
          <div className='border rounded-3 p-4'>
            <div className='row g-3 align-items-end'>
              {renderPeriodFilters()}
            </div>
          </div>
        </div>
        <div className='px-5 pb-5 bg-white'>
          {usePivotLayout ? (
            <>
              {/* Pivot tables: one per month, vessels as columns, fields as rows */}
              {loading ? (
                <div className='py-4'>
                  <TableSkeleton rows={6} cols={6} />
                </div>
              ) : pivotMonthKeys.length === 0 || pivotVessels.length === 0 ? (
                <div className='text-center py-10 text-muted'>No reports found</div>
              ) : (
                <div className='d-flex flex-column gap-4'>
                  {pivotMonthKeys.map((mk) => (
                    <div
                      key={mk}
                      className='report-table border rounded-3 overflow-hidden bg-white shadow-sm'
                    >
                      <div className='px-4 py-3 bg-light fw-bold text-dark border-bottom'>
                        {monthKeyToLabel(mk)}
                      </div>
                      <div style={{overflowX: 'auto'}}>
                        {renderPivotTable(mk, pivotVessels, pivotMap, {
                          canEditRow,
                          canDeleteRow,
                          canSubmitRow,
                          canReviewRow,
                          handleOpenView,
                          handleOpenEdit,
                          handleDelete,
                          handleDownloadPdf,
                          requestSubmit,
                          requestReview,
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              {/* Master view: classic row layout */}
              <div className='table-responsive'>
                <table className='table table-bordered align-middle mb-0'>
                  <thead className='table-header text-start'>
                    <tr>
                      {showVesselColumnForTable && <th>Vessel</th>}
                      <th>Month/Year</th>
                      <th>Deadweight</th>
                      <th>Engine(s)</th>
                      <th>System</th>
                      <th>EPL Date</th>
                      <th>{speedConsLabel('Ballast Spd(speed/cons)')}</th>
                      <th>{speedConsLabel('Laden Spd(speed/cons)')}</th>
                      <th>{speedConsLabel('Ballast Spd(Eco)')}</th>
                      <th>{speedConsLabel('Laden Spd(Eco)')}</th>
                      <th>{speedConsLabel('Ballast Spd(83% MCR)')}</th>
                      <th>{speedConsLabel('Laden Spd(83% MCR)')}</th>
                      <th>Status</th>
                      <th style={{minWidth: 220}}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className='table-body'>
                    {loading ? (
                      <TableSkeleton rows={rowsPerPage || 8} cols={showVesselColumnForTable ? 14 : 13} />
                    ) : pageRecords.length === 0 ? (
                        <tr>
                          <td colSpan={showVesselColumnForTable ? 14 : 13} className='text-center py-10 text-muted'>
                            No reports found
                          </td>
                        </tr>
                      ) : (
                        pageRecords.map((r) => {
                          const engines = r.engineEntries && r.engineEntries.length > 0 
                            ? r.engineEntries 
                            : [{ engineMakeModel: '', kw: null }]
                          const rowCount = engines.length

                          return engines.map((e: any, eIdx: number) => (
                            <tr
                              key={`${r.id}-${eIdx}`}
                              className={`${eIdx > 0 ? 'engine-row-divider' : ''} ${
                                eIdx === rowCount - 1 ? 'month-row-divider' : ''
                              }`}
                            >
                              {showVesselColumnForTable && eIdx === 0 && (
                                <td rowSpan={rowCount} className='month-row-divider-cell'>
                                  {r.vesselName || '-'}
                                </td>
                              )}
                              {eIdx === 0 && (
                                <td rowSpan={rowCount} className='month-row-divider-cell'>
                                  {r.reportYear && r.reportMonth ? monthKeyToLabel(recordMonthKey(r)) : '-'}
                                </td>
                              )}
                              {eIdx === 0 && (
                                <td rowSpan={rowCount} className='month-row-divider-cell'>
                                  {r.deadweight || '-'}
                                </td>
                              )}
                              
                              {/* Engine Details (Individual Row) */}
                              <td>
                                <div
                                  className='d-flex align-items-center gap-1'
                                  style={ENGINE_CARD_STYLE}
                                >
                                  <span
                                    className='badge badge-circle fw-bold'
                                    style={{
                                      width: '16px',
                                      height: '16px',
                                      fontSize: '8px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      ...ENGINE_BADGE_STYLE,
                                    }}
                                  >
                                    {eIdx + 1}
                                  </span>
                                  <div className='d-flex align-items-center' style={{minWidth: 0, flex: 1, overflow: 'hidden'}}>
                                    <div className='fw-semibold text-dark text-nowrap' style={{fontSize: '10px', lineHeight: '1.1', overflow: 'hidden', textOverflow: 'ellipsis'}}>{e.engineMakeModel || 'Engine'}</div>
                                  </div>
                                </div>
                              </td>

                              {eIdx === 0 && (
                                <td rowSpan={rowCount} className='month-row-divider-cell'>
                                  {r.systemType || '-'}
                                </td>
                              )}
                              {eIdx === 0 && (
                                <td rowSpan={rowCount} className='month-row-divider-cell'>
                                  {r.eplImplementationDate || '-'}
                                </td>
                              )}

                              {/* Engine-level Performance Fields (Individual Row) */}
                              <td className='text-center'>{renderSpeedConsumption(e.ballastSpeed12, e.ballastCons12, e.ballastRpm12, 'ballast')}</td>
                              <td className='text-center'>{renderSpeedConsumption(e.ladenSpeed12, e.ladenCons12, e.ladenRpm12, 'laden')}</td>
                              <td className='text-center'>{renderSpeedConsumption(e.ballastSpeedEco, e.ballastConsEco, e.ballastRpmEco, 'ballast')}</td>
                              <td className='text-center'>{renderSpeedConsumption(e.ladenSpeedEco, e.ladenConsEco, e.ladenRpmEco, 'laden')}</td>

                              {eIdx === 0 && (
                                <td rowSpan={rowCount} className='month-row-divider-cell'>
                                  {renderSpeedConsumption(r.ballastSpeed83, r.ballastCons83, r.ballastRpm83, 'ballast')}
                                </td>
                              )}
                              {eIdx === 0 && (
                                <td rowSpan={rowCount} className='month-row-divider-cell'>
                                  {renderSpeedConsumption(r.ladenSpeed83, r.ladenCons83, r.ladenRpm83, 'laden')}
                                </td>
                              )}

                              {eIdx === 0 && (
                                <td rowSpan={rowCount} className='month-row-divider-cell'>
                                  <span
                                    className={`badge badge-light-${
                                      r.status === 'REVIEWED'
                                        ? 'success'
                                        : r.status === 'SUBMITTED'
                                        ? 'warning'
                                        : 'primary'
                                    } fw-bold`}
                                  >
                                    {r.status || 'DRAFT'}
                                  </span>
                                </td>
                              )}
                              {eIdx === 0 && (
                                <td rowSpan={rowCount} className='month-row-divider-cell'>
                                  <div className='d-flex gap-1 align-items-center flex-wrap justify-content-end'>
                                    {r.status === 'REVIEWED' && (
                                      <button
                                        className='btn btn-icon btn-sm'
                                        title='Download PDF'
                                        onClick={() => handleDownloadPdf(r)}
                                      >
                                        <KTSVG path='/media/icons/duotune/general/download.svg' className='svg-icon-3 text-success' />
                                      </button>
                                    )}
                                    <button
                                      className='btn btn-icon btn-sm'
                                      title='View'
                                      onClick={() => handleOpenView(r)}
                                    >
                                      <KTSVG path='/media/map/ph_eye.svg' className='svg-icon-3 text-primary' />
                                    </button>
                                    {canEditRow(r) && (
                                      <button
                                        className='btn btn-icon btn-sm'
                                        title='Edit'
                                        onClick={() => handleOpenEdit(r)}
                                      >
                                        <KTSVG path='/media/map/edit-active.svg' className='svg-icon-3' />
                                      </button>
                                    )}
                                    {canDeleteRow(r) && (
                                      <button
                                        className='btn btn-icon btn-sm'
                                        title='Delete'
                                        onClick={() => handleDelete(r)}
                                      >
                                        <KTSVG path='/media/map/red-cross-icon.svg' className='svg-icon-3' />
                                      </button>
                                    )}
                                    {canSubmitRow(r) && (
                                      <button
                                        className='btn btn-light-primary btn-sm'
                                        onClick={() => requestSubmit(Number(r.id))}
                                      >
                                        Submit
                                      </button>
                                    )}
                                    {canReviewRow(r) && (
                                      <button
                                        className='btn btn-light-success btn-sm'
                                        onClick={() => requestReview(r)}
                                      >
                                        Review
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))
                        })
                      )}
                    </tbody>
                  </table>
                </div>
            </>
          )}
          {/* Pagination for master classic layout */}
          {!usePivotLayout && records.length > 0 && (
            <div
              className='pagination-wrapper d-flex justify-content-between align-items-center py-3 border-top px-4'
              style={{position: 'static', bottom: 0, backgroundColor: '#fff', zIndex: 10}}
            >
              <div className='d-flex align-items-center'>
                <span className='text-muted me-2 fs-7'>Rows per page</span>
                <SearchableSelect
                  className='form-select'
                  style={{
                    borderRadius: '20px',
                    width: '70px',
                    border: '1px solid #dee2e6',
                    fontSize: '14px',
                    padding: '4px 8px',
                  }}
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value))
                    setCurrentPage(1)
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </SearchableSelect>
              </div>

              <div className='d-flex align-items-center'>
                <span className='text-muted me-3 fs-7'>
                  Showing{' '}
                  <strong>
                    {records.length ? (currentPage - 1) * rowsPerPage + 1 : 0}-
                    {Math.min(currentPage * rowsPerPage, records.length)}
                  </strong>{' '}
                  of <strong>{records.length}</strong>
                </span>

                <nav>
                  <ul className='pagination pagination-sm mb-0' style={{gap: '2px'}}>
                    <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                      <button
                        className='page-link text-muted'
                        style={{
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #dee2e6',
                          padding: '8px 12px',
                          fontSize: '14px',
                          borderRadius: '6px',
                        }}
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        ‹
                      </button>
                    </li>
                    {Array.from({length: totalPages}, (_, i) => i + 1).map((i) => (
                      <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
                        <button
                          className='page-link text-muted'
                          style={{
                            backgroundColor: currentPage === i ? '#F4F9FF' : 'transparent',
                            border: '1px solid #dee2e6',
                            padding: '8px 12px',
                            fontSize: '14px',
                            minWidth: '40px',
                            borderRadius: '6px',
                            outline: 'none',
                            boxShadow: 'none',
                          }}
                          onClick={() => setCurrentPage(i)}
                        >
                          {i}
                        </button>
                      </li>
                    ))}
                    <li className={`page-item ${currentPage >= totalPages ? 'disabled' : ''}`}>
                      <button
                        className='page-link text-muted'
                        style={{
                          backgroundColor: '#f8f9fa',
                          border: '1px solid #dee2e6',
                          padding: '8px 12px',
                          fontSize: '14px',
                          borderRadius: '6px',
                        }}
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage >= totalPages}
                      >
                        ›
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {modalMode && (
        <div
          className='modal fade show d-block'
          tabIndex={-1}
          style={{backgroundColor: 'rgba(0,0,0,0.5)'}}
        >
          <div className='modal-dialog modal-xl modal-dialog-scrollable'>
            <div className='modal-content'>
              <div className='modal-header'>
                <div className='d-flex flex-column flex-grow-1'>
                  <h5 className='modal-title fw-bold mb-0'>
                    {modalMode === 'add' ? 'Add Report' : modalMode === 'edit' ? 'Edit Report' : 'View Report'}
                  </h5>
                  {modalMode === 'view' && (
                    <div className='text-muted fs-8 mt-1'>
                      Vessel: <span className='fw-bold text-dark'>{form.vesselName || '-'}</span>
                      <span className='mx-2'>•</span>
                      Month: <span className='fw-bold text-dark'>{form.reportYear && form.reportMonth ? monthKeyToLabel(`${form.reportYear}-${String(form.reportMonth).padStart(2, '0')}`) : '-'}</span>
                    </div>
                  )}
                </div>
                <div className='d-flex align-items-center gap-3'>
                  {modalMode === 'view' && (
                    <span className={`badge badge-light-${
                      form.status === 'REVIEWED' ? 'success' : form.status === 'SUBMITTED' ? 'warning' : 'primary'
                    } fw-bold`}>
                      {form.status || 'DRAFT'}
                    </span>
                  )}
                  <button
                    type='button'
                    className='btn-close'
                    onClick={() => setModalMode(null)}
                  ></button>
                </div>
              </div>
              <div className='modal-body'>
                {modalMode === 'view' ? (
                  <div className='p-2'>
                    {/* Basic Info Section */}
                    <div className='mb-6'>
                      <h6 className='text-primary fw-bold mb-4 border-bottom pb-2'>General Information</h6>
                      <div className='row'>
                        {renderViewField('Vessel', form.vesselName)}
                        {renderViewField('Month / Year', form.reportYear && form.reportMonth ? monthKeyToLabel(`${form.reportYear}-${String(form.reportMonth).padStart(2, '0')}`) : '-')}
                        {renderViewField('Deadweight', form.deadweight)}
                        {renderViewField('Actual EPL Implementation Date', form.eplImplementationDate)}
                        {renderViewField('System', form.systemType)}
                      </div>
                    </div>

                    {/* Engines Section */}
                    <div className='mb-6'>
                      <h6 className='text-primary fw-bold mb-4 border-bottom pb-2'>Main Engine Details & Performance</h6>
                      {(form.engineEntries || []).map((entry, idx) => (
                        <div className='border rounded p-4 mb-5 bg-light' key={idx}>
                          <div className='d-flex align-items-center gap-3 mb-4'>
                            <span className='badge badge-circle badge-primary fw-bold' style={{width: '24px', height: '24px'}}>
                              {idx + 1}
                            </span>
                            <h5 className='mb-0 fw-bold text-dark'>{entry.engineMakeModel || 'Main Engine'}</h5>
                            <span className='badge badge-light-dark fw-semibold'>{entry.kw ?? '-'} KW</span>
                          </div>

                          <div className='row g-4'>
                            <div className='col-md-6'>
                              <div className='border rounded p-3 bg-white h-100'>
                                <label className='fw-bold text-primary fs-8 d-block mb-3 border-bottom pb-1'>Present Day Observed Speed & Consumption</label>
                                <div className='row'>
                                  <div className='col-6 border-end'>
                                    <span className='fw-semibold text-muted fs-9 d-block mb-1'>BALLAST</span>
                                    <div className='fs-7 mb-1'><span className='text-dark fw-bold'>{entry.ballastSpeed12 ?? '-'}</span> <span className='text-muted fs-9'>kn</span></div>
                                    <div className='fs-7 mb-1'><span className='text-dark fw-bold'>{entry.ballastCons12 ?? '-'}</span> <span className='text-muted fs-9'>t/day</span></div>
                                    <div className='fs-7'><span className='text-dark fw-bold'>{entry.ballastRpm12 ?? '-'}</span> <span className='text-muted fs-9'>RPM</span></div>
                                  </div>
                                  <div className='col-6 ps-4'>
                                    <span className='fw-semibold text-muted fs-9 d-block mb-1'>LADEN</span>
                                    <div className='fs-7 mb-1'><span className='text-dark fw-bold'>{entry.ladenSpeed12 ?? '-'}</span> <span className='text-muted fs-9'>kn</span></div>
                                    <div className='fs-7 mb-1'><span className='text-dark fw-bold'>{entry.ladenCons12 ?? '-'}</span> <span className='text-muted fs-9'>t/day</span></div>
                                    <div className='fs-7'><span className='text-dark fw-bold'>{entry.ladenRpm12 ?? '-'}</span> <span className='text-muted fs-9'>RPM</span></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            <div className='col-md-6'>
                              <div className='border rounded p-3 bg-white h-100'>
                                <label className='fw-bold text-success fs-8 d-block mb-3 border-bottom pb-1'>Ballast and Laden Eco</label>
                                <div className='row'>
                                  <div className='col-6 border-end'>
                                    <span className='fw-semibold text-muted fs-9 d-block mb-1'>BALLAST</span>
                                    <div className='fs-7 mb-1'><span className='text-dark fw-bold'>{entry.ballastSpeedEco ?? '-'}</span> <span className='text-muted fs-9'>kn</span></div>
                                    <div className='fs-7 mb-1'><span className='text-dark fw-bold'>{entry.ballastConsEco ?? '-'}</span> <span className='text-muted fs-9'>t/day</span></div>
                                    <div className='fs-7'><span className='text-dark fw-bold'>{entry.ballastRpmEco ?? '-'}</span> <span className='text-muted fs-9'>RPM</span></div>
                                  </div>
                                  <div className='col-6 ps-4'>
                                    <span className='fw-semibold text-muted fs-9 d-block mb-1'>LADEN</span>
                                    <div className='fs-7 mb-1'><span className='text-dark fw-bold'>{entry.ladenSpeedEco ?? '-'}</span> <span className='text-muted fs-9'>kn</span></div>
                                    <div className='fs-7 mb-1'><span className='text-dark fw-bold'>{entry.ladenConsEco ?? '-'}</span> <span className='text-muted fs-9'>t/day</span></div>
                                    <div className='fs-7'><span className='text-dark fw-bold'>{entry.ladenRpmEco ?? '-'}</span> <span className='text-muted fs-9'>RPM</span></div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* 83% MCR Section */}
                    <div className='mb-6'>
                      <h6 className='text-primary fw-bold mb-4 border-bottom pb-2'>MAX Consumption Basis 83% Power MCR Lim</h6>
                      <div className='row g-4'>
                        <div className='col-md-6'>
                          <div className='border rounded p-3 bg-light-primary'>
                            <span className='fw-bold text-primary fs-8 d-block mb-2'>BALLAST</span>
                            <div className='row'>
                              {renderViewField('Speed (kn)', form.ballastSpeed83, 4)}
                              {renderViewField('Consumption (t/day)', form.ballastCons83, 4)}
                              {renderViewField('RPM', form.ballastRpm83, 4)}
                            </div>
                          </div>
                        </div>
                        <div className='col-md-6'>
                          <div className='border rounded p-3 bg-light-success'>
                            <span className='fw-bold text-success fs-8 d-block mb-2'>LADEN</span>
                            <div className='row'>
                              {renderViewField('Speed (kn)', form.ladenSpeed83, 4)}
                              {renderViewField('Consumption (t/day)', form.ladenCons83, 4)}
                              {renderViewField('RPM', form.ladenRpm83, 4)}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Reference Fields */}
                    <div className='mb-6'>
                      <h6 className='text-primary fw-bold mb-4 border-bottom pb-2'>Reference / EEXI Data</h6>
                      <div className='row'>
                        {renderViewField('Max Power (EPL MCR lim)', form.maxPowerEplMcrLim, 4)}
                        {renderViewField('Max Consumption (Ref EEXI)', form.maxConsumptionRefEexi, 4)}
                        {renderViewField('Design SFOC (gm/Kw-Hr)', form.designSfocFromEexi, 4)}
                        {renderViewField('Actual SFOC (at 83% EPL)', form.actualSfoc83, 4)}
                        {renderViewField('New Attainable Power (Pme)', form.newAttainablePower, 4)}
                        {renderViewField('New Speed Vref (kn)', form.newSpeedVref, 4)}
                      </div>
                    </div>

                    {/* Office Review Section */}
                    {(form.officeRemarks || form.status === 'REVIEWED') && (
                      <div className='mb-2'>
                        <h6 className='text-primary fw-bold mb-4 border-bottom pb-2'>Office Review & History</h6>
                        <div className='border rounded p-4 bg-light-warning'>
                          <label className={VIEW_LABEL}>Office Remarks</label>
                          <StructuredRemarksView text={form.officeRemarks} emptyMessage='No office remarks provided.' />
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    {isGlobalScope && modalMode === 'add' && (
                      <div className='row'>
                        <div className={showModalSubCompany ? 'col-md-6 mb-4' : 'col-md-12 mb-4'}>
                          <label className='form-label fw-semibold fs-7 text-dark'>Company</label>
                          <SearchableSelect
                            className='form-select form-select-sm text-dark'
                            style={{backgroundColor: '#fff'}}
                            value={modalCompanyGroup}
                            onChange={async (e) => {
                              const nextCompanyGroup = e.target.value
                              setModalCompanyGroup(nextCompanyGroup)
                              setModalCompanyAdmin('')
                              const nextVessels = getModalFilteredVessels(nextCompanyGroup, '')
                              const nextVesselId = nextVessels[0]?.id ? Number(nextVessels[0].id) : null
                                const next = await loadMainEngineConsumptionForm(
                                  nextVesselId,
                                  form.reportMonth,
                                  form.reportYear,
                                  {
                                    ...form,
                                    vesselId: nextVesselId,
                                  }
                                )
                                setForm(next.form)
                                setEditId(next.editId)
                                setModalMode(next.mode)
                            }}
                          >
                            <option value=''>All Companies</option>
                            {companyAdmins.map((c) => (
                              <option key={c.id} value={c.id}>
                                {c.name}
                              </option>
                            ))}
                          </SearchableSelect>
                        </div>
                        {showModalSubCompany && (
                          <div className='col-md-6 mb-4'>
                            <label className='form-label fw-semibold fs-7 text-dark'>Sub-Company</label>
                            <SearchableSelect
                              className='form-select form-select-sm text-dark'
                              style={{backgroundColor: '#fff'}}
                              value={modalCompanyAdmin}
                              onChange={async (e) => {
                                const nextCompanyAdmin = e.target.value
                                setModalCompanyAdmin(nextCompanyAdmin)
                                const nextVessels = getModalFilteredVessels(
                                  modalCompanyGroup,
                                  nextCompanyAdmin
                                )
                                const nextVesselId = nextVessels[0]?.id ? Number(nextVessels[0].id) : null
                                const next = await loadMainEngineConsumptionForm(
                                  nextVesselId,
                                  form.reportMonth,
                                  form.reportYear,
                                  {
                                    ...form,
                                    vesselId: nextVesselId,
                                  }
                                )
                                setForm(next.form)
                                setEditId(next.editId)
                                setModalMode(next.mode)
                              }}
                            >
                              <option value=''>All Sub-Companies</option>
                              {modalSubCompanies.map((c) => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </SearchableSelect>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Vessel, Month */}
                    <div className='row'>
                      {modalMode === 'add' && !canMultiMonthForShipboard && (
                        <div className='col-md-6 mb-4'>
                          <label className='form-label fw-semibold fs-7 text-dark'>
                            Vessel <span className='text-danger'>*</span>
                          </label>
                          <SearchableSelect
                            className='form-select form-select-sm text-dark'
                            style={{backgroundColor: '#fff'}}
                            value={form.vesselId ?? ''}
                            disabled={isReadOnly}
                            onChange={async (e) => {
                              const nextVesselId = e.target.value ? Number(e.target.value) : null
                              const next = await loadMainEngineConsumptionForm(
                                nextVesselId,
                                form.reportMonth,
                                form.reportYear,
                                {
                                  ...form,
                                  vesselId: nextVesselId,
                                }
                              )
                              setForm(next.form)
                              setEditId(next.editId)
                              setModalMode(next.mode)
                            }}
                          >
                            <option value=''>Select Vessel</option>
                            {vesselOptions.map((v: any) => (
                              <option key={v.id} value={v.id}>
                                {v.fleet_name || v.name}
                              </option>
                            ))}
                          </SearchableSelect>
                        </div>
                      )}
                      <div
                        className={
                          modalMode !== 'add' || canMultiMonthForShipboard ? 'col-md-12 mb-4' : 'col-md-6 mb-4'
                        }
                      >
                        <label className='form-label fw-semibold fs-7 text-dark'>
                          Month <span className='text-danger'>*</span>
                        </label>
                        <input
                          type='month'
                          className='form-control form-control-sm text-dark'
                          style={{backgroundColor: '#fff'}}
                          max={canMultiMonthForShipboard ? `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}` : undefined}
                          value={
                            form.reportYear && form.reportMonth
                              ? `${form.reportYear}-${String(form.reportMonth).padStart(2, '0')}`
                              : ''
                          }
                          disabled={isReadOnly}
                          onChange={(e) => {
                            if (!e.target.value) {
                              setField('reportMonth', null)
                              setField('reportYear', null)
                              return
                            }

                            const [y, m] = e.target.value.split('-')
                            const nextMonth = Number(m)
                            const nextYear = Number(y)

                            setForm((prev) => ({
                              ...prev,
                              reportMonth: nextMonth,
                              reportYear: nextYear,
                            }))

                            if (!form.vesselId) return

                            void loadMainEngineConsumptionForm(form.vesselId, nextMonth, nextYear, {
                              ...form,
                              reportMonth: nextMonth,
                              reportYear: nextYear,
                            }).then((next) => {
                              setForm(next.form)
                              setEditId(next.editId)
                              setModalMode(next.mode)
                            })
                          }}
                        />
                      </div>
                    </div>

                    {/* Deadweight, EPL Date, System */}
                    <div className='row'>
                      <div className='col-md-4 mb-4'>
                        <label className='form-label fw-semibold fs-7 text-dark'>
                          Deadweight
                          <span className='badge badge-light-info ms-2 fs-9'>Auto</span>
                        </label>
                        <input
                          type='text'
                          className='form-control form-control-sm text-dark'
                          style={{backgroundColor: '#f8f9fa'}}
                          value={form.deadweight || ''}
                          disabled={isReadOnly}
                          readOnly
                          title='Auto-filled from Vessel Additional Info – General Information – Summer DWT'
                          onChange={(e) => setField('deadweight', e.target.value)}
                        />
                      </div>
                      <div className='col-md-4 mb-4'>
                        <label className='form-label fw-semibold fs-7 text-dark'>
                          Actual EPL Implementation Date
                          <span className='badge badge-light-info ms-2 fs-9'>One-time</span>
                        </label>
                        <input
                          type='date'
                          className='form-control form-control-sm text-dark'
                          style={{backgroundColor: '#fff'}}
                          value={form.eplImplementationDate || ''}
                          disabled={isReadOnly}
                          onChange={(e) => setField('eplImplementationDate', e.target.value || null)}
                        />
                      </div>
                      <div className='col-md-4 mb-4'>
                        <label className='form-label fw-semibold fs-7 text-dark'>System</label>
                        <SearchableSelect
                          className='form-select form-select-sm text-dark'
                          style={{backgroundColor: '#fff'}}
                          value={form.systemType || ''}
                          disabled={isReadOnly}
                          onChange={(e) => setField('systemType', e.target.value || null)}
                        >
                          <option value=''>Select System</option>
                          <option value='EPL'>EPL</option>
                          <option value='SHAPOLI'>Shapoli</option>
                        </SearchableSelect>
                      </div>
                    </div>

                    {/* Engine entries (repeatable) */}
                    <div className='separator my-4'></div>
                    <div className='d-flex justify-content-between align-items-center mb-3'>
                      <h6 className='fw-bold mb-0'>
                        Main Engine Make / Model and KW
                        <span className='badge badge-light-info ms-2 fs-9'>One-time</span>
                      </h6>
                      {!isReadOnly && (
                        <button className='btn btn-sm btn-light-primary' onClick={addEngineEntry}>
                          <i className='bi bi-plus-lg me-1'></i>Add Engine
                        </button>
                      )}
                    </div>
                    {(form.engineEntries || []).map((entry, idx) => (
                      <div className='border rounded p-4 mb-4 bg-light-light' key={idx}>
                        <div className='row align-items-end mb-4'>
                          <div className='col-md-9'>
                            <label className='form-label fw-bold fs-7 text-dark'>Make / Model and KW</label>
                            <input
                              type='text'
                              className='form-control form-control-sm text-dark border-primary'
                              style={{backgroundColor: '#fff'}}
                              value={entry.engineMakeModel || ''}
                              disabled={isReadOnly}
                              onChange={(e) => setEngineEntry(idx, 'engineMakeModel', e.target.value)}
                            />
                          </div>
                          {!isReadOnly && (form.engineEntries?.length ?? 0) > 1 && (
                            <div className='col-md-3 text-end'>
                              <button
                                className='btn btn-sm btn-light-danger'
                                onClick={() => removeEngineEntry(idx)}
                              >
                                <i className='bi bi-trash me-1'></i>Remove Engine
                              </button>
                            </div>
                          )}
                        </div>

                        {/* Nested Speed/Consumption fields */}
                        <div className='row'>
                          <div className='col-12'>
                            <h6 className='fw-bold text-dark mb-3 border-bottom pb-2'>
                              Present Day Observed Speed and Consumption
                            </h6>
                          </div>
                          <div className='col-12 mb-2'>
                            <span className='badge badge-light-primary fw-bold'>Ballast</span>
                          </div>
                          {nestedNumInput(idx, 'ballastSpeed12', 'Speed (kn)', entry.ballastSpeed12)}
                          {nestedNumInput(idx, 'ballastCons12', 'Consumption (t/day)', entry.ballastCons12)}
                          {nestedNumInput(idx, 'ballastRpm12', 'RPM', entry.ballastRpm12)}

                          <div className='col-12 mb-2 mt-2'>
                            <span className='badge badge-light-success fw-bold'>Laden</span>
                          </div>
                          {nestedNumInput(idx, 'ladenSpeed12', 'Speed (kn)', entry.ladenSpeed12)}
                          {nestedNumInput(idx, 'ladenCons12', 'Consumption (t/day)', entry.ladenCons12)}
                          {nestedNumInput(idx, 'ladenRpm12', 'RPM', entry.ladenRpm12)}
                        </div>

                        <div className='row mt-4'>
                          <div className='col-12'>
                            <h6 className='fw-bold text-dark mb-3 border-bottom pb-2'>
                              Ballast and Laden Eco
                            </h6>
                          </div>
                          <div className='col-12 mb-2'>
                            <span className='badge badge-light-primary fw-bold'>Ballast</span>
                          </div>
                          {nestedNumInput(idx, 'ballastSpeedEco', 'Speed (kn)', entry.ballastSpeedEco)}
                          {nestedNumInput(idx, 'ballastConsEco', 'Consumption (t/day)', entry.ballastConsEco)}
                          {nestedNumInput(idx, 'ballastRpmEco', 'RPM', entry.ballastRpmEco)}

                          <div className='col-12 mb-2 mt-2'>
                            <span className='badge badge-light-success fw-bold'>Laden</span>
                          </div>
                          {nestedNumInput(idx, 'ladenSpeedEco', 'Speed (kn)', entry.ladenSpeedEco)}
                          {nestedNumInput(idx, 'ladenConsEco', 'Consumption (t/day)', entry.ladenConsEco)}
                          {nestedNumInput(idx, 'ladenRpmEco', 'RPM', entry.ladenRpmEco)}
                        </div>
                      </div>
                    ))}

                    {/* Speed/Cons at 83% MCR */}
                    <div className='separator my-4'></div>
                    <h6 className='fw-bold mb-3'>
                      MAX Consumption and Speed — Basis ME at 83% Power of the MCR Lim
                    </h6>
                    <div className='row'>
                      <div className='col-12'>
                        <p className='fw-semibold text-primary mb-2'>Ballast</p>
                      </div>
                      {numInput('ballastSpeed83', 'Speed', form.ballastSpeed83)}
                      {numInput('ballastCons83', 'Consumption', form.ballastCons83)}
                      {numInput('ballastRpm83', 'RPM', form.ballastRpm83)}
                    </div>
                    <div className='row'>
                      <div className='col-12'>
                        <p className='fw-semibold text-success mb-2'>Laden</p>
                      </div>
                      {numInput('ladenSpeed83', 'Speed', form.ladenSpeed83)}
                      {numInput('ladenCons83', 'Consumption', form.ladenCons83)}
                      {numInput('ladenRpm83', 'RPM', form.ladenRpm83)}
                    </div>

                    {/* Reference fields */}
                    <div className='separator my-4'></div>
                    <h6 className='fw-bold mb-3'>Reference / EEXI Fields</h6>
                    <div className='row'>
                      {numInput(
                        'maxPowerEplMcrLim',
                        'Max Power (EPL MCR lim) – Ref EEXI',
                        form.maxPowerEplMcrLim
                      )}
                      {numInput(
                        'maxConsumptionRefEexi',
                        'Max Consumption – Ref EEXI',
                        form.maxConsumptionRefEexi
                      )}
                      {/* Design SFOC – auto-populated one-time field */}
                      <div className='col-md-4 col-lg-3 mb-4'>
                        <label className='form-label fw-semibold fs-7 text-dark'>
                          Design SFOC (gm/Kw-Hr) – EEXI
                          <span className='badge badge-light-info ms-2 fs-9'>One-time</span>
                        </label>
                        <input
                          type='number'
                          step='any'
                          className='form-control form-control-sm text-dark'
                          style={{backgroundColor: '#fff'}}
                          value={form.designSfocFromEexi ?? ''}
                          disabled={isReadOnly}
                          onChange={(e) =>
                            setField(
                              'designSfocFromEexi',
                              e.target.value ? Number(e.target.value) : null
                            )
                          }
                        />
                      </div>
                      {numInput('actualSfoc83', 'Actual SFOC (gm/Kw-Hr) at 83% EPL', form.actualSfoc83)}
                      {numInput(
                        'newAttainablePower',
                        'New Attainable Power (Pme) KW – 83% MCR',
                        form.newAttainablePower
                      )}
                      {numInput('newSpeedVref', 'New Speed Vref (Knots) – Ref EEXI', form.newSpeedVref)}
                    </div>
                    {isReadOnly &&
                      (form.officeRemarks ||
                        form.status === 'SUBMITTED' ||
                        form.status === 'REVIEWED') && (
                        <>
                          <div className='separator my-4'></div>
                          <h6 className='fw-bold mb-3'>Office Review</h6>
                          <div className='mb-2'>
                            <label className='form-label fw-semibold fs-7 text-dark'>
                              Office Remarks
                            </label>
                            <StructuredRemarksView text={form.officeRemarks} emptyMessage='No office remarks provided.' />
                          </div>
                        </>
                      )}
                  </>
                )}
              </div>
              <div className='modal-footer'>
                <button className='btn btn-sm btn-light' onClick={() => setModalMode(null)}>
                  {isReadOnly ? 'Close' : 'Cancel'}
                </button>
                {!isReadOnly && (
                  <button className='btn btn-sm btn_primary' onClick={handleSave}>
                    <i className='bi bi-check-lg me-1'></i>
                    {modalMode === 'add' ? 'Create' : 'Update'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteMainEngineReportModal
        visible={!!deletingRecord}
        onClose={() => setDeletingRecord(null)}
        onConfirm={confirmDelete}
      />

      <MainEngineActionConfirmModal
        visible={!!submitTargetId}
        title='Submit this report to Office?'
        subtitle='You can still review it from office workflow after submission.'
        confirmLabel='Yes, Submit'
        confirmClassName='btn btn_primary'
        onClose={() => setSubmitTargetId(null)}
        onConfirm={confirmSubmit}
      />

      <MainEngineReviewModal
        visible={!!reviewingRecord}
        record={reviewingRecord}
        isLoading={isReviewSaving}
        onClose={handleCloseReviewModal}
        onSave={handleSaveReviewOnly}
        onCloseSatisfactory={handleCloseSatisfactory}
      />

      <MainEngineTrialAttachmentsModal
        visible={!!trialModal}
        trialType={trialModal?.type ?? null}
        vesselId={trialModal?.vesselId ?? null}
        vesselName={trialModal?.vesselName}
        canEdit={canUpdate}
        canUpload={canUpdate && (isShipboardUser || isGlobalScope)}
        onClose={() => setTrialModal(null)}
      />
    </>
  )
}

export default MainEngineConsumptionPage
